/**
 * ServerFullLogsDrawer — Drawer right 80% pour afficher les logs complets.
 *
 * - Charge à l'ouverture via `useCustom` sur /full-logs.
 * - Toolbar : reload / download / copy / wrap.
 * - Auto-scroll bas à l'ouverture.
 * - Warning si tronqué à 10 MB (flag `truncated` backend).
 *
 * @spec specs/app/admin/dedicated-servers.md §6.8
 */

import { useCustom } from '@refinedev/core';
import { Alert, App as AntdApp, Button, Drawer, Tooltip } from 'antd';
import { Copy, Download, RefreshCw, WrapText } from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type FC } from 'react';

interface ServerFullLogsDrawerProps {
  serverId: string;
  serverName: string;
  open: boolean;
  onClose: () => void;
}

interface FullLogsResponse {
  content?: string;
  logs?: string;
  truncated?: boolean;
  bytes?: number;
}

const ServerFullLogsDrawer: FC<ServerFullLogsDrawerProps> = ({
  serverId,
  serverName,
  open,
  onClose,
}) => {
  const { message } = AntdApp.useApp();
  const [wrap, setWrap] = useState(true);
  const preRef = useRef<HTMLPreElement | null>(null);

  const { data, isLoading, isError, refetch } = useCustom<FullLogsResponse>({
    url: `/api/v1/admin/dedicated-servers/${serverId}/full-logs`,
    method: 'get',
    queryOptions: { enabled: open, retry: 0 },
  });

  const { content, truncated } = useMemo<{ content: string; truncated: boolean }>(() => {
    const payload = data?.data as FullLogsResponse | string | undefined;
    if (typeof payload === 'string') return { content: payload, truncated: false };
    if (payload && typeof payload === 'object') {
      return {
        content: payload.content ?? payload.logs ?? '',
        truncated: Boolean(payload.truncated),
      };
    }
    return { content: '', truncated: false };
  }, [data]);

  // Auto-scroll bas à l'ouverture
  useEffect(() => {
    if (!open || !preRef.current || !content) return;
    preRef.current.scrollTop = preRef.current.scrollHeight;
  }, [open, content]);

  const handleDownload = () => {
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${serverName}-provisioning-logs.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(content);
      void message.success('Logs copiés dans le presse-papiers');
    } catch {
      void message.error('Copie échouée');
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      placement="right"
      width="80%"
      title={`Logs complets — ${serverName}`}
      extra={
        <div className="flex items-center gap-2">
          <Tooltip title="Recharger">
            <Button
              size="small"
              icon={<RefreshCw className="h-3.5 w-3.5" />}
              onClick={() => void refetch()}
            />
          </Tooltip>
          <Tooltip title="Télécharger .txt">
            <Button
              size="small"
              icon={<Download className="h-3.5 w-3.5" />}
              onClick={handleDownload}
              disabled={!content}
            />
          </Tooltip>
          <Tooltip title="Copier tout">
            <Button
              size="small"
              icon={<Copy className="h-3.5 w-3.5" />}
              onClick={() => void handleCopy()}
              disabled={!content}
            />
          </Tooltip>
          <Tooltip title={wrap ? 'Désactiver word-wrap' : 'Activer word-wrap'}>
            <Button
              size="small"
              icon={<WrapText className="h-3.5 w-3.5" />}
              type={wrap ? 'primary' : 'default'}
              onClick={() => setWrap((v) => !v)}
            />
          </Tooltip>
        </div>
      }
    >
      {truncated && (
        <Alert
          className="mb-3"
          type="warning"
          showIcon
          message="Logs tronqués à 10 MB"
          description="Pour consulter l'intégralité, téléchargez le fichier complet (bouton Télécharger .txt)."
        />
      )}

      {isError ? (
        <Alert
          type="error"
          showIcon
          message="Impossible de charger les logs"
          description="Vérifier que l'endpoint GET /api/v1/admin/dedicated-servers/:id/full-logs répond."
        />
      ) : isLoading ? (
        <div className="flex items-center justify-center py-8 text-sm text-slate-400">
          Chargement des logs…
        </div>
      ) : (
        <pre
          ref={preRef}
          className={`h-[calc(100vh-180px)] overflow-auto rounded-md border border-slate-200 bg-slate-950 p-3 font-mono text-xs text-slate-100 dark:border-slate-700 ${
            wrap ? 'whitespace-pre-wrap break-words' : 'whitespace-pre'
          }`}
        >
          {content || '— (logs vides) —'}
        </pre>
      )}
    </Drawer>
  );
};

export default ServerFullLogsDrawer;
